import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import {
    Autocomplete, Box, Button, IconButton, Paper, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography,
} from "@mui/material"
import { useEffect } from "react"
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { IItem, IOrder, IOrderMixEntry, SetValueFn } from "./types"

export default function MixSection({ control, setValue, getValues, mixType, totalPctName, totalValName, title, items, loadingItems }: {
    control: Control<IOrder>
    setValue: SetValueFn
    getValues: () => IOrder
    mixType: "primary" | "secondary"
    totalPctName: "order_mix_total_percentage" | "order_secondary_mix_total_percentage"
    totalValName: "order_mix_total_value" | "order_secondary_mix_total_value"
    title: string
    items: IItem[]
    loadingItems: boolean
}) {
    const { fields, append, update } = useFieldArray({ control, name: "order_mix_entries" })
    const allEntries = useWatch({ control, name: "order_mix_entries" }) as IOrderMixEntry[]
    const totalProgrammed = useWatch({ control, name: "general_info_programmed_total" }) as number

    const recalcTotals = (entries: IOrderMixEntry[]) => {
        const active = entries.filter(e => !e._destroy && e.mix_type === mixType)
        setValue(totalPctName, active.reduce((s, e) => s + Number(e.percentage_value), 0))
        setValue(totalValName, active.reduce((s, e) => s + Number(e.total_value), 0))
    }

    useEffect(() => {
        const entries = getValues().order_mix_entries
        const total = Number(totalProgrammed)
        entries.forEach((e, i) => {
            if (e.mix_type !== mixType || e._destroy) return
            setValue(`order_mix_entries.${i}.total_value`, total * (Number(e.percentage_value) / 100))
        })
        recalcTotals(getValues().order_mix_entries)
    }, [totalProgrammed])

    useEffect(() => {
        if (!items.length) return
        getValues().order_mix_entries.forEach((e, i) => {
            if (e.mix_type !== mixType) return
            const item = items.find(x => x.ID === e.item_id) ?? null
            if (item) setValue(`order_mix_entries.${i}.item`, item)
        })
    }, [items])

    const handleAdd = () => {
        append({ id: 0, item_id: "", item_name: "", item: null, percentage_value: 0, total_value: 0, mix_type: mixType, _destroy: false })
    }

    const handleRemove = (index: number) => {
        const e = allEntries[index]
        update(index, { ...e, _destroy: true })
        recalcTotals(getValues().order_mix_entries)
    }

    const handleItemChange = (data: IItem | null, index: number) => {
        setValue(`order_mix_entries.${index}.item`, data)
        setValue(`order_mix_entries.${index}.item_id`, data?.ID ?? "")
        setValue(`order_mix_entries.${index}.item_name`, data?.Name ?? "")
    }

    const handlePctChange = (val: number, index: number) => {
        const tv = Number(getValues().general_info_programmed_total) * (val / 100)
        setValue(`order_mix_entries.${index}.percentage_value`, val)
        setValue(`order_mix_entries.${index}.total_value`, tv)
        recalcTotals(getValues().order_mix_entries)
    }

    const activeFields = fields
        .map((f, i) => ({ ...f, _idx: i }))
        .filter((_, i) => !(allEntries[i]?._destroy) && allEntries[i]?.mix_type === mixType)

    const totalPct = useWatch({ control, name: totalPctName }) as number
    const totalVal = useWatch({ control, name: totalValName }) as number

    const { t } = useTranslation()

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{title}</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width="60%">{t("orders.form.item")} {loadingItems && <Typography component="span" variant="caption" color="text.secondary">{t("orders.form.loadingItems")}</Typography>}</TableCell>
                            <TableCell width="20%">%</TableCell>
                            <TableCell width="15%" align="right">Total (Kgr)</TableCell>
                            <TableCell width="5%" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingItems && activeFields.length > 0
                            ? activeFields.map(({ _idx: idx }) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton variant="rounded" height={32} /></TableCell>
                                    <TableCell><Skeleton variant="rounded" height={32} /></TableCell>
                                    <TableCell><Skeleton variant="rounded" height={32} /></TableCell>
                                    <TableCell />
                                </TableRow>
                            ))
                            : activeFields.map(({ _idx: idx }) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Controller
                                            control={control}
                                            name={`order_mix_entries.${idx}.item` as keyof IOrder}
                                            render={({ field: { value } }) => (
                                                <Autocomplete
                                                    options={items}
                                                    value={(value as IItem | null) ?? null}
                                                    isOptionEqualToValue={(op, v) => op.ID === v.ID}
                                                    getOptionLabel={op => op.Name}
                                                    onChange={(_, data) => handleItemChange(data, idx)}
                                                    renderInput={params => <TextField {...params} label={t("orders.form.item")} size="small" />}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Controller
                                            control={control}
                                            name={`order_mix_entries.${idx}.percentage_value` as keyof IOrder}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    size="small"
                                                    type="number"
                                                    fullWidth
                                                    onChange={e => handlePctChange(Number(e.target.value), idx)}
                                                />
                                            )}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Controller
                                            control={control}
                                            name={`order_mix_entries.${idx}.total_value` as keyof IOrder}
                                            render={({ field: { value } }) => (
                                                <Typography variant="body2">{Number(value).toFixed(2)}</Typography>
                                            )}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" color="error" onClick={() => handleRemove(idx)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                        <TableRow>
                            <TableCell />
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>{Number(totalPct).toFixed(1)}%</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>{Number(totalVal).toFixed(2)}</TableCell>
                            <TableCell />
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={4} align="center">
                                <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={loadingItems}>
                                    {t("orders.form.addTo", { section: title })}
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}
